<template>
  <div class="borrowerAttributeBoolean">
    <BorrowerAttributeConstructor
      :questionText="questionText"
      :description="description"
      :descriptionTooltip="descriptionTooltip"
      :label="label"
      :tooltipText="tooltipText"
      :errorText="errorText"
      :helpText="helpText"
      :disclaimer="disclaimer">
      <!--
        FIXME:
        Something seems to be broken with this component generally.
        Does not show the input field like its supposed to?
        Though we aren't currently using this input really throughout the app.
      -->
      <template v-slot:description>
        <div class="row">
          <div class="long-bool-text mb-2" :class="columnWidth" :data-cy="question.alias + '-text'">
            <span v-html="description"></span>
            {{descriptionTooltip}}
            {{description}}
            <div class="tooltip-wrapper" v-if="descriptionTooltip">
              <fa-icon :icon="['far', 'circle-question']" class="fa-circle-question icon-circle-question"></fa-icon>
            </div>
          </div>
          <div class="col" :class="{'long-bool-radio': !customLabels}">
            <div class="form-check form-check-inline"
                 :class="{'radio-disabled': disabled, 'col mb-1': customLabels}"
                 v-for="option in options"
                 :key="option.value"
            >
              <input type="radio"
                     :id="'ba' + identifier + '-' + option.value"
                     class="form-check-input"
                     :class="{'is-invalid': v$.value && v$.value.$error}"
                     :name="'ba' + identifier"
                     v-model="value"
                     :value="option.value"
                     :disabled="disabled"
                     :required="question.required"
                     @change="onBlur(v$.value, $event)"
                     :data-cy="question.alias + '-' + option.value"
              >
              <label class="form-check-label" :class="{'sideButton': customLabels}" :for="'ba' + identifier + '-' + option.value">{{option.label}}</label>
              <span class="borrower-attribute-tooltip">
              <a v-if="customOptions && option.helpText">
              <div class="tooltip-wrapper">
                <fa-icon :icon="['far', 'circle-question']" class="fa-circle-question icon-circle-question"></fa-icon>
              </div>
              </a>
            </span>
            </div>
          </div>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
  import get from 'lodash/get'
  import { useVuelidate } from '@vuelidate/core'
  import { required, requiredIf } from '@vuelidate/validators'
  import { currency } from '~/libs/format'
  import BorrowerAttributeConstructor from './BorrowerAttributeConstructor.vue'

  export default {
    setup: () => ({ v$: useVuelidate() }),
    data () {
      return {
        value: '',
        errorMessage: 'Please select an answer',
        currency,
      }
    },
    props: {
      question: Object,
      disabled: Boolean,
      tooltipPlacement: String,
      businessType: String,
      averageMonthlyPayroll: String,
      portalAction: Boolean
    },
    emits: ['invalidity', 'answer'],
    created () {
      this.$watch('invalid', (invalid) => {
        this.$emit('invalidity', invalid)
      }, {immediate: true})

      this.value = this.question.value
    },
    components: {
      BorrowerAttributeConstructor
    },
    computed: {
      identifier() {
        return this.question.id || this.question.alias
      },
      invalid() {
        return this.v$.$invalid
      },
      questionText() {
        return this.question.questionText || ''
      },
      description() {
        let description
        switch (this.question.alias) {
          case 'currentBorrowerInterest':
          case 'blueVineCorpDocs':
          case 'hasW2Employees':
          case 'llcType':
          case 'scheduleCIncomeType':
            description = this.question.helpText
            break
          case 'payrollYearUsed':
            description = !this.question.calculated
              ? `You entered <b>${currency(this.averageMonthlyPayroll)}</b> for your average monthly payroll. What year did you use to calculate your payroll?`
              : `Your average monthly payroll is <b>${currency(this.averageMonthlyPayroll)}</b>. What year did you use to calculate your payroll?`
            break
          default:
            description = this.question.description || ''

        }
        return description
      },
      descriptionTooltip() {
        let descriptionTooltip
        switch (this.question.alias) {
          case 'scheduleCIncomeType':
            descriptionTooltip = this.question.tooltip
            break
          default:
            descriptionTooltip = this.question.descriptionTooltip || ''
        }
        return descriptionTooltip
      },
      label() {
        let label
        switch (this.question.alias) {
          case 'blueVineCorpDocs':
          case 'hasW2Employees':
          case 'llcType':
          case 'payrollYearUsed':
          case 'scheduleCIncomeType':
            label = ''
            break
          default:
            label = this.question.name || ''
        }
        return label
      },
      tooltipText() {
        let tooltipText
        switch (this.question.alias) {
          case 'currentBorrowerInterest':
          case 'blueVineCorpDocs':
          case 'hasW2Employees':
          case 'llcType':
          case 'scheduleCIncomeType':
            tooltipText = ''
            break
          default:
            tooltipText = this.question.tooltip || this.question.helpText || ''
        }
        return tooltipText
      },
      helpText() {
        let helpText
        switch (this.question.alias) {
          case 'scheduleCIncomeType':
            helpText = ''
            break
          default:
            helpText = this.question.tooltip ? this.question.helpText : ''
        }
        return helpText
      },
      errorText() {
        let errorText = get(this.v$, 'value.$error') ? this.errorMessage : ''
        return errorText
      },
      disclaimer() {
        let disclaimer = this.question.disclaimer || ''
        return disclaimer
      },
      options () {
        let options = [
          { label: 'Yes', value: '1' },
          { label: 'No', value: '0' }
        ]
        if (this.payrollYearAlias || this.customOptions) {
          options = this.question.options
        }
        return options
      },
      customOptions () {
        return this.question.alias === 'llcType' || (this.question.options && this.question.options.length >= 2)
      },
      payrollYearAlias () {
        return this.question.alias === 'payrollYearUsed'
      },
      customLabels () {
        return this.options.some(opt => !/yes|no/i.exec(opt.label))
      },
      columnWidth () {
        let columnWidth
        switch(true) {
          case this.customLabels:
            columnWidth = this.question.alias === 'payrollYearUsed'
              ? 'col-md-9 col-lg-9'
              : 'col-md-12 col-lg-12'
            break
          case this.portalAction:
            columnWidth = 'col-md-12 col-lg-12'
            break
          default:
            columnWidth = 'col-md-9 col-lg-9'
        }
        return columnWidth
      }
    },
    methods: {
      onBlur (v$, e) {
        v$.$touch()
        this.$emit('invalidity', v$.$invalid)
        this.$emit('answer', { value: e.target.value, isInvalid: v$.$error })
      }
    },
    validations() {
      let validations
      if (['blueVineCorpDocs'].includes(this.question.alias)) {
        validations = {
          value: {
            required: requiredIf(() => {
              return get(this.conditionalValidations, `${this.question.alias}.required`)
            }),
          }
        }
      } else {
        validations = {
          value: { required }
        }
      }

      return {
        ...validations,
      }
    }
  }
</script>

<style>
.form-check-input.is-invalid ~ .form-check-label:before {
  border: 1px solid #DC3545;
}
.form-check-input.is-invalid ~ .form-check-label {
  color: #000;
}
.long-bool-text {
	font-size: 13px;
}
.long-bool-radio {
	align-self: center;
}
.long-bool-radio div:first-child{
  margin-right: 2rem;
}
.long-bool-radio .form-check {
  min-height: 3.5rem;
}
.long-bool-radio .form-check-inline {
  padding-left: 0;
}

.long-bool-radio .form-check-label:before {
  top: 1.55rem;
  left: 0.1rem;
}

.long-bool-radio .form-check-label.sideButton:before {
  top: 0;
  left: 0;
}

.long-bool-radio .form-check-label:after {
  top: 1.55rem;
  left: 0.1rem;
}

.long-bool-radio .form-check-label.sideButton:after {
  top: 0;
  left: 0;
}

.residence-feedback {
  color: red;
  padding-left: 15px;
}
</style>

<style scoped lang="scss">

  .icon-circle-question {
    color: $blue;
    margin-left: 5px;
  }
  .vue-tooltip.tooltip-inner .tooltip-arrow {
    border-color: rgba(0, 0, 0, 0.90);
  }
  .tooltip-wrapper {
    position: relative;
    display: inline-block;
    .tooltip-trigger {
      width: 20px;
      height:16px;
      position: absolute;
      left: 5px;
      top: 3px
    }
  }
  .vue-tooltip.tooltip-inner .tooltip-arrow {
    border-color: rgba(0, 0, 0, 0.90);
  }
  .tooltip-wrapper {
    position: relative;
    display: inline-block;
    .tooltip-trigger {
      width: 20px;
      height:16px;
      position: absolute;
      left: 5px;
      top: 3px
    }
  }
  .description {
    font-size: 12px;
  }
</style>
<style>
</style>
